import { FC, ReactNode, useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../../lib/utils";

interface SectionCardProps {
    /** 섹션 제목 */
    title: string;
    /** 제목 왼쪽에 표시할 아이콘 (선택) */
    icon?: ReactNode;
    /** 아이템 개수 표시 (선택) */
    count?: number;
    /** 우측 액션 버튼들 */
    actions?: ReactNode;
    /** 섹션 내용 */
    children: ReactNode;
    /** 접기/펼치기 가능 여부 (기본: true) */
    collapsible?: boolean;
    /** 초기 펼침 상태 (기본: true) */
    defaultExpanded?: boolean;
    /** 커스텀 클래스 */
    className?: string;
    /** 아이템이 없을 때도 섹션을 표시할지 여부 */
    showEmpty?: boolean;
    /** 빈 상태일 때 표시할 메시지 */
    emptyMessage?: string;
}

export const SectionCard: FC<SectionCardProps> = ({
    title,
    icon,
    count,
    actions,
    children,
    collapsible = true,
    defaultExpanded = true,
    className,
    showEmpty = false,
    emptyMessage,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const toggleExpand = () => {
        if (collapsible) {
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <div className={cn(
            "w-full mb-1 bg-gray-50 rounded-lg px-4",
            className
        )}>
            {/* 헤더 */}
            <div className="flex items-center justify-between py-2 group">
                {/* 왼쪽: 제목 + 토글 */}
                <button
                    onClick={toggleExpand}
                    disabled={!collapsible}
                    className={cn(
                        "flex items-center gap-2 text-sm font-semibold text-gray-900",
                        collapsible && "hover:text-gray-600 transition-colors"
                    )}
                >
                    {icon && <span className="text-gray-500">{icon}</span>}
                    <span>{title}</span>
                    {count !== undefined && (
                        <span className="text-gray-400 font-normal text-xs">({count})</span>
                    )}
                    {collapsible && (
                        <span className="text-gray-400 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </span>
                    )}
                </button>

                {/* 오른쪽: 액션 버튼들 */}
                {actions && (
                    <div className="flex items-center gap-1">
                        {actions}
                    </div>
                )}
            </div>

            {/* 바디 */}
            {isExpanded && (
                <div className="pb-2 animate-in slide-in-from-top-1 duration-200">
                    {children}
                    {showEmpty && !children && emptyMessage && (
                        <p className="text-sm text-gray-400 italic py-2 text-center">
                            {emptyMessage}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

/** 섹션 카드 내부에서 사용하는 심플한 액션 버튼 */
export const SectionActionButton: FC<{
    onClick: () => void;
    icon?: ReactNode;
    label: string;
    variant?: "default" | "ghost" | "link";
}> = ({ onClick, icon, label, variant = "ghost" }) => {
    return (
        <Button
            variant={variant}
            size="sm"
            onClick={onClick}
            className="h-7 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
        >
            {icon && <span className="mr-1">{icon}</span>}
            {label}
        </Button>
    );
};

/** 리스트 아이템 래퍼 - 일관된 간격과 호버 효과 */
export const SectionListItem: FC<{
    children: ReactNode;
    onClick?: () => void;
    className?: string;
}> = ({ children, onClick, className }) => {
    return (
        <div
            onClick={onClick}
            className={cn(
                "flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg transition-colors",
                onClick && "cursor-pointer hover:bg-gray-50",
                className
            )}
        >
            {children}
        </div>
    );
};

export default SectionCard;
